import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthorizationStore } from '../store';

export function UserRoleSheet() {
  const {
    userRoleSheetOpen,
    selectedUserForRoleEdit,
    pendingUserRoleChanges,
    isSubmittingUserRoles,
    roles,
    closeUserRoleSheet,
    toggleUserRole,
    discardUserRoleChanges,
    saveUserRoles,
  } = useAuthorizationStore();

  if (!selectedUserForRoleEdit) return null;

  const isDefaultAdmin = selectedUserForRoleEdit.isDefaultAdmin;
  // 86d3d1z40: derive from the directly-subscribed `pendingUserRoleChanges` /
  // `selectedUserForRoleEdit` (deps the React Compiler can track) instead of calling the
  // store's getEffectiveUserRoles()/hasUserRolePendingChanges() in render — those read
  // state via get() internally, which the compiler can't see, so it memoized the role
  // checkboxes (checked) and the Save button (disabled) and left them stale after a toggle.
  // Mirrors authorizationStore.getEffectiveUserRoles (selectedUser.roles is objects → r.roleId).
  const effectiveRoleIds = new Set(selectedUserForRoleEdit.roles.map((r) => r.roleId));
  Object.entries(pendingUserRoleChanges).forEach(([roleIdStr, assigned]) => {
    const roleId = parseInt(roleIdStr, 10);
    if (assigned) effectiveRoleIds.add(roleId);
    else effectiveRoleIds.delete(roleId);
  });
  const effectiveRoles = Array.from(effectiveRoleIds);
  const pendingChangesCount = Object.keys(pendingUserRoleChanges).length;
  const hasPendingChanges = pendingChangesCount > 0;

  const handleCancel = () => {
    discardUserRoleChanges();
    closeUserRoleSheet();
  };

  const isRoleModified = (roleId: number) => {
    return pendingUserRoleChanges[roleId] !== undefined;
  };

  return (
    <Sheet open={userRoleSheetOpen} onOpenChange={(open) => !open && handleCancel()}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Edit User Roles</SheetTitle>
          <SheetDescription>
            Manage roles for {selectedUserForRoleEdit.username}
          </SheetDescription>
        </SheetHeader>

        {/* User Info Section */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">USER INFO</h4>
          <div className="space-y-1">
            <div className="text-sm">
              <span className="text-muted-foreground">Username:</span>{' '}
              <span className="font-medium">{selectedUserForRoleEdit.username}</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Name:</span>{' '}
              <span className="font-medium">
                {`${selectedUserForRoleEdit.firstName} ${selectedUserForRoleEdit.lastName}`.trim() || '-'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Email:</span>{' '}
              <span className="font-medium">{selectedUserForRoleEdit.email}</span>
            </div>
          </div>
        </div>

        <Separator className="my-4" />

        {isDefaultAdmin ? (
          <div className="flex-1 flex flex-col justify-start">
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                Role assignments for the default System Administrator account cannot be modified.
              </AlertDescription>
            </Alert>

            <div className="mt-4 space-y-2 overflow-y-auto pr-2">
              {effectiveRoles.map((roleId) => {
                const role = roles.find((r) => r.id === roleId);
                if (!role) return null;
                return (
                  <div
                    key={role.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30"
                  >
                    <Checkbox id={`role-${role.id}`} checked disabled />
                    <div className="flex-1 min-w-0">
                      <label htmlFor={`role-${role.id}`} className="text-sm font-medium text-muted-foreground">
                        {role.name}
                      </label>
                      <p className="text-xs text-muted-foreground">{role.roleCode}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Available Roles Section */
          <div className="flex-1 min-h-0 flex flex-col">
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex-shrink-0">
              AVAILABLE ROLES
            </h4>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {roles.map((role) => {
                const isAssigned = effectiveRoles.includes(role.id);
                const isModified = isRoleModified(role.id);

                return (
                  <div
                    key={role.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={isAssigned}
                      onCheckedChange={() => toggleUserRole(role.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <label
                        htmlFor={`role-${role.id}`}
                        className="text-sm font-medium cursor-pointer flex items-center gap-2"
                      >
                        {role.name}
                        {isModified && (
                          <Badge variant="outline" className="text-xs text-warning border-warning/30">
                            modified
                          </Badge>
                        )}
                      </label>
                      <p className="text-xs text-muted-foreground">{role.roleCode}</p>
                      {role.description && (
                        <p className="text-xs text-muted-foreground mt-1">{role.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}

              {roles.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  No roles available
                </div>
              )}
            </div>
          </div>
        )}

        <Separator className="my-4" />

        {/* Footer */}
        <div className="flex-shrink-0 space-y-3">
          {!isDefaultAdmin && hasPendingChanges && (
            <div className="text-sm text-warning">
              {pendingChangesCount} pending {pendingChangesCount === 1 ? 'change' : 'changes'}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={isSubmittingUserRoles}>
              {isDefaultAdmin ? 'Close' : 'Cancel'}
            </Button>
            {!isDefaultAdmin && (
              <Button
                disabled={!hasPendingChanges || isSubmittingUserRoles}
                onClick={async () => {
                  const success = await saveUserRoles();
                  if (success) {
                    toast.success('User roles updated successfully');
                  } else {
                    toast.error('Failed to update user roles');
                  }
                }}
              >
                {isSubmittingUserRoles && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
